import React, { useState } from "react";
import { Search, Plus, ChevronLeft, ChevronRight as ChevronRightIcon } from "lucide-react";
import FreeTrialCard from "./FreeTrialCard";
import { useTranslation } from '../../contexts/LanguageContext'; // Add this import

const FreeTrialList = ({ freeTrials, onAddSubscription, refreshFreeTrials }) => {
  const { t } = useTranslation(); // Add this hook
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const formatDate = (dateString) => {
    if (!dateString) return t('common.na', 'N/A');
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const filteredTrials = freeTrials.filter(trial => 
    trial.merchant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trial.plan_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTrials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTrials = filteredTrials.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleTrialCancelled = () => {
    if (refreshFreeTrials) {
      refreshFreeTrials();
    }
  };

  return (
    <div className="space-y-4 pb-8">
      {/* Header with search */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between p-1">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('freeTrialList.searchPlaceholder', 'Search free trials...')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Free Trial Cards */}
      <div className="space-y-3 isolate">
        {currentTrials.length > 0 ? (
          currentTrials.map((trial) => (
            <FreeTrialCard 
              key={trial.id} 
              trial={trial} 
              onTrialCancelled={handleTrialCancelled}
            />
          ))
        ) : (
          <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-gray-200">
            <div className="mx-auto w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-inner mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#253985]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <p className="text-gray-600 text-lg mb-2">
              {searchTerm ? 
                t('freeTrialList.noSearchResults', 'No free trials match your search') : 
                t('freeTrialList.noActiveTrials', 'No active free trials found')}
            </p>
            <p className="text-gray-500 text-sm">
              {t('freeTrialList.exploreMarketplace', 'Explore our marketplace to find services offering free trials')}
            </p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredTrials.length > itemsPerPage && (
        <div className="flex items-center justify-between mt-6 px-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center px-4 py-2 rounded-lg transition-all ${
              currentPage === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
            }`}
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            {t('common.previous', 'Previous')}
          </button>
          
          <div className="flex items-center space-x-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                    currentPage === pageNum 
                      ? 'bg-white text-[#253985] border-2 border-[#253985] font-bold shadow-sm' 
                      : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center px-4 py-2 rounded-lg transition-all ${
              currentPage === totalPages 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
            }`}
          >
            {t('common.next', 'Next')}
            <ChevronRightIcon className="h-5 w-5 ml-2" />
          </button>
        </div>
      )}
    </div>
  );
};

export default FreeTrialList;